import { useCallback, useEffect, useState } from "react";
import { AndroidVerificationCard } from "@/components/market/AndroidVerification";
import { ReviewsCard } from "@/components/admin/ReviewsCard";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Boxes, Check, Globe2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useDevAuth } from "@/context/DevAuthContext";
import { PageHero } from "@/components/layout/PageHero";
import { AppFormDialog } from "@/components/admin/AppFormDialog";
import { PromptLibraryCard } from "@/components/admin/PromptLibrary";
import { ConfirmButton } from "@/components/admin/ConfirmButton";
import { AppsPanel } from "@/components/admin/AppsPanel";
import { IncompleteAlert } from "@/components/dev/IncompleteAlert";
import { DriveSharePanel } from "@/components/admin/DriveSharePanel";
import { WarningBlock } from "@/components/market/WarningBlock";
import { BillingCard } from "@/components/dev/BillingCard";
import { PLANS, usePrice } from "@/components/market/Plans";
import { translateError } from "@/i18n/errors";
import { ClientDashboard } from "@/components/dev/ClientDashboard";
import { RoleSwitch } from "@/components/dev/RoleSwitch";

const Step = ({ n, done, title, children }) => (
  <li className={`flex flex-col gap-3 rounded-2xl border p-4 ${done ? "border-emerald-500/40 bg-emerald-950/20" : "border-[rgba(245,197,66,0.3)] bg-black/30"}`} data-testid={`dev-step-${n}`}>
    <div className="flex items-center gap-3">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display font-black ${done ? "bg-emerald-500 text-black" : "bg-[var(--gold)] text-black"}`}>
        {done ? <Check className="h-4 w-4" /> : n}
      </span>
      <p className="font-semibold text-white" data-testid={`dev-step-${n}-title`}>{title}</p>
    </div>
    {!done && children}
  </li>
);

const DangerZone = () => {
  const { t } = useI18n();
  const { setDev } = useDevAuth();
  const remove = async () => {
    try {
      await api.delete("/dev/account");
      toast.success(t("dev.deleted"));
      setDev(false);
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <section className="rounded-2xl border border-red-700/60 bg-red-950/30 p-6" data-testid="dev-danger-zone">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-red-200"><AlertTriangle className="h-5 w-5" /> {t("dev.dangerTitle")}</h2>
      <p className="mt-2 text-sm text-red-100/80">{t("dev.dangerText")}</p>
      <ConfirmButton title={t("dev.deleteTitle")} description={t("dev.deleteDesc")} onConfirm={remove} className="btn-red btn-sm mt-4" testId="dev-delete-account-btn">
        <Trash2 className="h-4 w-4" /> {t("dev.deleteAccount")}
      </ConfirmButton>
    </section>
  );
};

export default function DevDashboard() {
  const { dev } = useDevAuth();
  return dev.kind === "minisite" ? <ClientDashboard /> : <DeveloperDashboard />;
}

function DeveloperDashboard() {
  const { t } = useI18n();
  const { price, k } = usePrice();
  const { dev, refresh } = useDevAuth();
  const [params, setParams] = useSearchParams();
  const [apps, setApps] = useState(null);
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(dev.billing);
  const cancelled = params.get("paiement") === "annule";

  const [reloadKey, setReloadKey] = useState(0);
  const load = useCallback(() => setReloadKey((n) => n + 1), []);
  const onLoaded = useCallback(({ items, total }) => { if (total !== null) setApps({ items, total }); refresh(); }, [refresh]);
  const incomplete = apps?.items.find((a) => a.platform?.incomplete);
  useEffect(() => {
    api.get("/dev/billing").then(({ data }) => setBilling(data)).catch(() => {});
  }, [load]);

  const active = billing.can_publish;
  const withVersion = apps?.items.find((a) => a.active_version);
  const firstApp = apps?.items[0];
  return (
    <div data-testid="dev-dashboard">
      <PageHero eyebrow={t("dev.eyebrow")} title={t("dev.welcome", { name: dev.brand })}>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/apps" className="btn-gold btn-sm btn-cta" data-testid="dev-view-site-btn"><Globe2 className="h-4 w-4" /> {t("dev.viewSite")}</Link>
          <Link to={`/developpeurs/${dev.slug}`} className="btn-red btn-sm btn-cta" data-testid="dev-public-page-link">{t("dev.publicPage")}</Link>
          <RoleSwitch />
        </div>
      </PageHero>
      <div className="mx-auto max-w-7xl space-y-8 px-4 sm:px-8">
        {cancelled && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/60 bg-red-950/60 px-4 py-3 text-sm text-red-100" role="alert" data-testid="payment-cancelled-notice">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {t("billing.cancelled")}</span>
            <button type="button" className="text-xs underline" onClick={() => setParams({})} data-testid="payment-cancelled-dismiss">{t("dev.dismiss")}</button>
          </div>
        )}
        {(dev.incomplete_count > 0 || incomplete) && (
          <IncompleteAlert count={dev.incomplete_count || 1} to={incomplete ? `/espace-developpeur/apps/${incomplete.id}?completer=1` : null} testId="dev-incomplete-alert" />
        )}
        <WarningBlock testId="dev-space-warning" />
        <section data-testid="dev-guided-steps">
          <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]">{t("dev.stepsTitle")}</h2>
          <ol className="grid gap-3 md:grid-cols-3">
            <Step n={1} done={active} title={t("dev.step1")}>
              <a href="#billing" className="btn-gold btn-sm btn-cta self-start" data-testid="dev-step-1-btn">{t(k("billing.subscribe"), { price: price(PLANS.find((p) => p.id === billing?.plan) || PLANS[0]) })}</a>
            </Step>
            <Step n={2} done={!!apps?.total} title={t("dev.step2")}>
              <button type="button" className="btn-gold btn-sm btn-cta self-start" onClick={() => setOpen(true)} data-testid="dev-step-2-btn"><Plus className="h-4 w-4" /> {t("admin.newApp")}</button>
            </Step>
            <Step n={3} done={!!withVersion} title={t("dev.step3")}>
              {firstApp ? (
                <Link to={`/espace-developpeur/apps/${firstApp.id}`} className="btn-gold btn-sm btn-cta self-start" data-testid="dev-step-3-btn"><Upload className="h-4 w-4" /> {t("upload.submit")}</Link>
              ) : <p className="text-xs text-neutral-500">{t("dev.step3Hint")}</p>}
            </Step>
          </ol>
        </section>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0 space-y-3">
            {!active && <p className="text-xs text-amber-200" data-testid="dev-publish-locked">{t("err.subRequired")}</p>}
            <AppsPanel prefix="dev" maxApps={billing.max_apps || 200} reloadKey={reloadKey} onLoaded={onLoaded} />
          </div>
          <aside className="min-w-0 space-y-6">
            <div id="billing"><BillingCard billing={billing} onChange={(b) => { setBilling(b); refresh(); }} /></div>
            <section className="card-carbon rounded-2xl p-6" data-testid="dev-sandbox-card">
              <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><Boxes className="h-5 w-5 text-[var(--gold)]" /> {t("dev.sandboxTitle")}</h2>
              <p className="mt-2 break-all text-xs text-neutral-400" data-testid="dev-sandbox-name">{dev.sandbox?.name}</p>
              <p className="mt-2 text-sm text-neutral-400">{t("dev.sandboxText")}</p>
            </section>
            <AndroidVerificationCard />
            <ReviewsCard />
            <DriveSharePanel />
            <PromptLibraryCard />
            <DangerZone />
          </aside>
        </div>
      </div>
      {open && <AppFormDialog open={open} onOpenChange={setOpen} onSaved={load} />}
    </div>
  );
}
